<template>
  <v-col cols="6">
    <v-card>
      <v-card-title class="green text-center">
        <v-icon color="white" size="50" class="mr-3">mdi-car-clock</v-icon>
        <span class="text-h5 white--text text-center">Driving Test Dates</span>
      </v-card-title>
      <v-card-text>
        <v-data-table
          :headers="drivingTestDateHeaders"
          :items="displayedDrivingTests"
          :loading="isloading"
          :items-per-page="5"
          class="elevation-1 mt-3"
          no-data-text="No upcoming driving tests recorded yet."
        >
          <template v-slot:[`item.test_date`]="{ item }">
            {{ item.test_date }}
          </template>
          <template v-slot:[`item.test_time`]="{ item }">
            {{ item.test_time }}
          </template>
          <template v-slot:[`item.confirmation_number`]="{ item }">
            {{ formatConfirmationNumber(item) }}
          </template>
          <template v-slot:[`item.status`]="{ item }">
            <v-chip :color="getStatusColor(item)" small>
              {{ getStatusText(item) }}
            </v-chip>
          </template>
          <template v-slot:[`item.actions`]="{ item }">
            <v-btn
              color="error"
              x-small
              :disabled="!isActionActive(item)"
              @click="cancelDrivingTest(item)"
            >
              Cancel
            </v-btn>
          </template>
        </v-data-table>

        <v-divider class="my-4"></v-divider>

        <v-form class="ma-2">
          <v-container>
            <v-divider class="mt-3 mb-5"></v-divider>
            <h3 class="mb-3">Record Upcoming Driving Test</h3>
            <v-row>
              <v-col md="8">
                <v-menu
                  v-model="dateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="form.test_date"
                      name="test_date"
                      label="Driving test date"
                      readonly
                      required
                      v-bind="attrs"
                      v-on="on"
                      :error-messages="testDateError"
                      @blur="$v.form.test_date.$touch()"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="form.test_date"
                    locale="en-gb"
                    :min="minDate"
                    :max="maxDate"
                    no-title
                    @input="dateMenu = false"
                    @change="$v.form.test_date.$touch()"
                  ></v-date-picker>
                </v-menu>
              </v-col>
              <v-col md="4">
                <v-text-field
                  v-model="form.test_time"
                  name="test_time"
                  label="Driving test time"
                  required
                  type="time"
                  :error-messages="testTimeError"
                  @input="$v.form.test_time.$touch()"
                  @blur="$v.form.test_time.$touch()"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="form.confirmation_number"
                  name="confirmation_number"
                  label="Test confirmation number"
                  hint="Optional 18-digit confirmation number"
                  maxlength="18"
                  persistent-hint
                  :error-messages="confirmationNumberError"
                  @input="$v.form.confirmation_number.$touch()"
                  @blur="$v.form.confirmation_number.$touch()"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" class="d-flex align-center justify-end">
                <v-btn
                  color="warning"
                  :disabled="isRecordBtn"
                  @click="saveDrivingTestDate"
                >
                  Record Test Date
                </v-btn>
              </v-col>
            </v-row>
          </v-container>
        </v-form>
      </v-card-text>
    </v-card>
  </v-col>
</template>

<script>
import { required } from "vuelidate/lib/validators";
import { mapActions } from "vuex";
import {
  SET_DRIVING_TEST_DATE,
  CANCEL_DRIVING_TEST_DATE,
  M_OU,
} from "@/store/mutation-list";

const toIsoDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getTodayIso = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return toIsoDate(today);
};

const getMaxFutureIso = () => {
  const futureDate = new Date();
  futureDate.setHours(0, 0, 0, 0);
  futureDate.setMonth(futureDate.getMonth() + 12);
  return toIsoDate(futureDate);
};

const isAllowedDrivingDate = (value) => {
  if (!value) {
    return true;
  }

  return value >= getTodayIso() && value <= getMaxFutureIso();
};

export default {
  name: "DrivingTestDate",
  props: ["learner", "testdatedata", "isloading"],
  data() {
    return {
      drivingTestDateHeaders: [
        {
          text: "Test ID",
          align: "start",
          sortable: false,
          value: "id",
        },
        { text: "Test Date", value: "test_date" },
        { text: "Test Time", value: "test_time" },
        { text: "Confirmation No.", value: "confirmation_number" },
        { text: "Status", value: "status", sortable: false },
        { text: "Actions", value: "actions", sortable: false },
      ],
      dateMenu: false,
      form: {
        test_date: null,
        test_time: null,
        confirmation_number: null,
      },
    };
  },
  validations: {
    form: {
      test_date: {
        required,
        isAllowedDrivingDate,
      },
      test_time: {
        required,
      },
      confirmation_number: {
        isEighteenDigits(value) {
          if (!value) {
            return true;
          }

          return /^\d{18}$/.test(String(value));
        },
      },
    },
  },
  computed: {
    minDate() {
      return getTodayIso();
    },
    maxDate() {
      return getMaxFutureIso();
    },
    displayedDrivingTests() {
      if (Array.isArray(this.testdatedata)) {
        return this.testdatedata;
      }

      return [];
    },
    testDateError() {
      const errors = [];
      if (!this.$v.form.test_date.$dirty) return errors;
      !this.$v.form.test_date.required &&
        errors.push("Driving test date is required.");
      !this.$v.form.test_date.isAllowedDrivingDate &&
        errors.push(
          "Driving test date must be from today up to 12 months ahead."
        );
      return errors;
    },
    testTimeError() {
      const errors = [];
      if (!this.$v.form.test_time.$dirty) return errors;
      !this.$v.form.test_time.required &&
        errors.push("Driving test time is required.");
      return errors;
    },
    confirmationNumberError() {
      const errors = [];
      if (!this.$v.form.confirmation_number.$dirty) return errors;
      !this.$v.form.confirmation_number.isEighteenDigits &&
        errors.push("Confirmation number must contain exactly 18 digits.");
      return errors;
    },
    isRecordBtn() {
      return this.$v.$invalid;
    },
  },
  methods: {
    ...mapActions({
      saveTestDate: `${M_OU}${SET_DRIVING_TEST_DATE}`,
      cancelTestDate: `${M_OU}${CANCEL_DRIVING_TEST_DATE}`,
    }),
    getDateTimeValue(testDate, testTime) {
      if (!testDate) {
        return null;
      }

      const normalizedDate = String(testDate).trim().slice(0, 10);
      let normalizedTime = "00:00";

      if (testTime) {
        const match = String(testTime)
          .trim()
          .match(/^(\d{1,2}):(\d{2})/);
        if (match) {
          normalizedTime = `${match[1].padStart(2, "0")}:${match[2]}`;
        }
      }

      const parsedValue = new Date(
        `${normalizedDate}T${normalizedTime}:00`
      ).getTime();
      return Number.isNaN(parsedValue) ? null : parsedValue;
    },
    isPastTestDate(testDate, testTime) {
      const dateTimeValue = this.getDateTimeValue(testDate, testTime);
      if (!dateTimeValue) {
        return false;
      }

      return dateTimeValue < Date.now();
    },
    isFutureTestDate(testDate, testTime) {
      const dateTimeValue = this.getDateTimeValue(testDate, testTime);
      if (!dateTimeValue) {
        return false;
      }

      return dateTimeValue > Date.now();
    },
    getStatusText(item) {
      const status = Number(item.status);
      if (status === 0) {
        return "Cancelled";
      }

      if (status === 1) {
        if (this.isPastTestDate(item.test_date, item.test_time)) {
          return "Completed";
        }

        return "Scheduled";
      }

      return "Unknown";
    },
    getStatusColor(item) {
      const statusText = this.getStatusText(item);
      if (statusText === "Cancelled") {
        return "red lighten-2";
      }
      if (statusText === "Completed") {
        return "blue lighten-2";
      }

      return "cyan lighten-2";
    },
    isActionActive(item) {
      return (
        Number(item.status) === 1 &&
        this.isFutureTestDate(item.test_date, item.test_time)
      );
    },
    formatConfirmationNumber(item) {
      return (
        item.confirmation_number ||
        item.confirmation_no ||
        item.confirmation ||
        "-"
      );
    },
    resetForm() {
      this.form.test_date = null;
      this.form.test_time = null;
      this.form.confirmation_number = null;
      this.dateMenu = false;
      this.$v.$reset();
    },
    saveDrivingTestDate() {
      if (this.$v.$invalid) {
        this.$v.$touch();
        return;
      }

      if (!this.learner) {
        this.$fire({
          title: "Error",
          text: "No learner ID attached.",
          type: "error",
          allowOutsideClick: false,
        });
        return;
      }

      const newRecord = {
        id: `DT-${Date.now()}`,
        test_date: this.form.test_date,
        test_time: this.form.test_time,
        confirmation_number: this.form.confirmation_number,
        status: 1,
      };

      this.saveTestDate({
        learner: this.learner,
        testDate: this.form.test_date,
        testTime: this.form.test_time,
        confirmationNumber: this.form.confirmation_number,
      })
        .then((res) => {
          if (res == "SUCCESS") {
            this.$fire({
              title: "Success",
              text: "Learner driving test date recorded.",
              type: "success",
              timer: 3000,
            });

            this.$emit("save-test-date", {
              learner: this.learner,
              record: newRecord,
            });

            this.resetForm();
          } else {
            this.$fire({
              title: "Error",
              text: "Error recording Learner Driving Test Date. Please try again.",
              type: "error",
              allowOutsideClick: false,
            });
          }
        })
        .catch(() => {
          this.$fire({
            title: "Error",
            text: "Error recording Learner Driving Test Date. Please try again.",
            type: "error",
            allowOutsideClick: false,
          });
        });
    },
    cancelDrivingTest(item) {
      if (!item || !this.isActionActive(item)) {
        return;
      }

      const cancelledRecord = {
        ...item,
        status: 0,
      };

      this.$fire({
        title: "Cancel driving test date?",
        text: "This will mark the test as cancelled.",
        showCancelButton: true,
        confirmButtonText: "Yes, cancel",
        type: "question",
        allowOutsideClick: false,
      }).then((r) => {
        if (!r.value) {
          return;
        }

        this.cancelTestDate({
          learner: this.learner,
          testDateId: item.id,
        })
          .then((res) => {
            if (res == "SUCCESS") {
              this.$fire({
                title: "Success",
                text: "Learner driving test date cancelled.",
                type: "success",
                timer: 3000,
              });

              this.$emit("cancel-test-date", {
                learner: this.learner,
                record: cancelledRecord,
              });
            } else {
              this.$fire({
                title: "Error",
                text: "Error cancelling Learner Driving Test Date. Please try again.",
                type: "error",
                allowOutsideClick: false,
              });
            }
          })
          .catch(() => {
            this.$fire({
              title: "Error",
              text: "Error cancelling Learner Driving Test Date. Please try again.",
              type: "error",
              allowOutsideClick: false,
            });
          });
      });
    },
  },
};
</script>
